/**
 * Scan Queue — Books marked "Scan" awaiting agent assignment
 *
 * Shows all bookstore_books with status='scan' and no assigned_agent_id.
 * Admin checks library availability via catalog links, marks available/not,
 * then assigns to a scanning agent (Atherton=SMCL, FiDi=NYPL).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, ExternalLink, Loader2, UserCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';
import { toast } from 'sonner';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bookstoreTable = () => (supabase as any).from('bookstore_books');

interface ScanQueueBook {
  id: string;
  title: string;
  author: string | null;
  publisher: string | null;
  isbn: string | null;
  cover_image_url: string | null;
  source_url: string;
  smcl_available: boolean | null;
  nypl_available: boolean | null;
  library_data: Record<string, unknown> | null;
  assigned_agent_id: string | null;
  source: 'bookstore' | 'parent_request';
  requested_by_name?: string | null;
}

interface ScanningAgent {
  id: string;
  name: string;
  email: string | null;
}

export function ScanQueue() {
  const [books, setBooks] = useState<ScanQueueBook[]>([]);
  const [agents, setAgents] = useState<ScanningAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [expandedBook, setExpandedBook] = useState<string | null>(null);
  const [filterAvailability, setFilterAvailability] = useState<'all' | 'unchecked' | 'smcl' | 'nypl' | 'both'>('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch from both sources in parallel
      const [bookstoreRes, requestsRes, agentsRes] = await Promise.all([
        // 1. Bookstore books marked "scan" without assigned agent
        bookstoreTable()
          .select('id, title, author, publisher, isbn, cover_image_url, source_url, smcl_available, nypl_available, library_data, assigned_agent_id')
          .eq('status', 'scan')
          .is('assigned_agent_id', null)
          .order('title', { ascending: true }),
        // 2. Parent-requested books with status 'pending' (not yet assigned)
        supabase
          .from('book_scan_requests')
          .select('id, title, author, isbn, cover_image_url, publisher, requested_by, status, assigned_to')
          .eq('status', 'pending')
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
        // 3. Scanning agents
        supabase
          .from('profiles')
          .select('id, name, email')
          .eq('admin_level', 'scanning_agent'),
      ]);

      if (bookstoreRes.error) throw new Error(bookstoreRes.error.message);

      // Map bookstore books
      const bookstoreBooks: ScanQueueBook[] = (bookstoreRes.data || []).map((b: Record<string, unknown>) => ({
        ...b,
        source: 'bookstore' as const,
      }));

      // Map parent requests to same shape
      const requestBooks: ScanQueueBook[] = (requestsRes.data || []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        title: r.title as string || 'Untitled',
        author: r.author as string | null,
        publisher: r.publisher as string | null,
        isbn: r.isbn as string | null,
        cover_image_url: r.cover_image_url as string | null,
        source_url: '',
        smcl_available: null,
        nypl_available: null,
        library_data: null,
        assigned_agent_id: r.assigned_to as string | null,
        source: 'parent_request' as const,
      }));

      setBooks([...requestBooks, ...bookstoreBooks]);
      setAgents(agentsRes.data || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSetAvailability = async (bookId: string, library: 'smcl' | 'nypl', available: boolean | null) => {
    setActionInProgress(bookId);
    try {
      const field = library === 'smcl' ? 'smcl_available' : 'nypl_available';
      const { error } = await bookstoreTable()
        .update({ [field]: available, updated_at: new Date().toISOString() })
        .eq('id', bookId);

      if (error) { toast.error(error.message); return; }

      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, [field]: available } : b));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleAssignAgent = async (bookId: string, agentId: string) => {
    const book = books.find(b => b.id === bookId);
    if (!book) return;

    setActionInProgress(bookId);
    try {
      if (book.source === 'parent_request') {
        // Update book_scan_requests
        const { error } = await supabase
          .from('book_scan_requests')
          .update({ assigned_to: agentId, status: 'assigned', assigned_at: new Date().toISOString(), updated_at: new Date().toISOString() })
          .eq('id', bookId);
        if (error) { toast.error(error.message); return; }
      } else {
        // Update bookstore_books
        const { error } = await bookstoreTable()
          .update({ assigned_agent_id: agentId, updated_at: new Date().toISOString() })
          .eq('id', bookId);
        if (error) { toast.error(error.message); return; }
      }

      // Remove from list (assigned = no longer in queue)
      setBooks(prev => prev.filter(b => b.id !== bookId));
      const agent = agents.find(a => a.id === agentId);
      toast.success(`Assigned to ${agent?.name || 'agent'}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleSaveLibraryData = async (bookId: string, data: Record<string, unknown>) => {
    const { error } = await bookstoreTable()
      .update({ library_data: data, updated_at: new Date().toISOString() })
      .eq('id', bookId);

    if (error) { toast.error(error.message); return; }

    setBooks(prev => prev.map(b => b.id === bookId ? { ...b, library_data: data } : b));
    toast.success('Library data saved');
  };

  // Catalog search URLs
  const smclSearchUrl = (isbn: string) => `https://smcl.bibliocommons.com/v2/search?query=${isbn}&searchType=isbn`;
  const nyplSearchUrl = (isbn: string) => `https://nypl.bibliocommons.com/v2/search?query=${isbn}&searchType=isbn`;

  const filtered = useMemo(() => {
    let list = books;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(b =>
        b.title.toLowerCase().includes(q) ||
        (b.author && b.author.toLowerCase().includes(q)) ||
        (b.isbn && b.isbn.includes(q))
      );
    }
    if (filterAvailability === 'unchecked') list = list.filter(b => b.smcl_available === null && b.nypl_available === null);
    if (filterAvailability === 'smcl') list = list.filter(b => b.smcl_available === true);
    if (filterAvailability === 'nypl') list = list.filter(b => b.nypl_available === true);
    if (filterAvailability === 'both') list = list.filter(b => b.smcl_available === true && b.nypl_available === true);
    return list;
  }, [books, searchTerm, filterAvailability]);

  const stats = useMemo(() => ({
    total: books.length,
    unchecked: books.filter(b => b.smcl_available === null && b.nypl_available === null).length,
    smclAvailable: books.filter(b => b.smcl_available === true).length,
    nyplAvailable: books.filter(b => b.nypl_available === true).length,
    noIsbn: books.filter(b => !b.isbn).length,
  }), [books]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#1A1A1A] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[#1C1C1E]">Scan Queue</h2>
        <p className="text-sm text-[#8E8E93] mt-0.5">
          {stats.total} books to assign &middot; {stats.unchecked} unchecked &middot; {stats.smclAvailable} in SMCL &middot; {stats.nyplAvailable} in NYPL &middot; {stats.noIsbn} missing ISBN
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" />
          <input
            type="text"
            placeholder="Search title, author, ISBN..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-black/[0.08] rounded-xl text-[#1C1C1E] text-sm placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]"
          />
        </div>

        <div className="flex gap-1 flex-wrap">
          {([
            ['all', 'All'],
            ['unchecked', 'Unchecked'],
            ['smcl', 'SMCL Available'],
            ['nypl', 'NYPL Available'],
            ['both', 'Both Available'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setFilterAvailability(value)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                filterAvailability === value
                  ? 'bg-[#111111] text-white'
                  : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.06]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
          <p className="text-[#8E8E93] text-sm">
            {books.length === 0
              ? 'No books in the scan queue. Mark books as "Scan" in Bookstore Books first.'
              : 'No books match your current filters.'}
          </p>
        </div>
      )}

      {/* Book list */}
      {filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(book => {
            const isActioning = actionInProgress === book.id;
            const isExpanded = expandedBook === book.id;
            const hasIsbn = !!book.isbn;

            return (
              <div key={book.id} className="rounded-2xl p-4" style={glassStyles.card}>
                <div className="flex gap-4">
                  {/* Cover */}
                  {book.cover_image_url && (
                    <img
                      src={book.cover_image_url}
                      alt={book.title}
                      className="w-14 h-20 object-cover rounded-lg shrink-0 bg-black/[0.03]"
                    />
                  )}

                  {/* Book info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#1C1C1E] truncate">{book.title}</h3>
                    {book.author && <p className="text-sm text-[#636366]">{book.author}</p>}
                    <div className="flex items-center gap-2 mt-0.5">
                      {book.isbn && <span className="text-xs font-mono text-[#8E8E93]">ISBN: {book.isbn}</span>}
                      {!book.isbn && <span className="text-xs text-[#FF9F0A]">No ISBN</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        book.source === 'parent_request'
                          ? 'bg-[#111111]/10 text-[#1A1A1A]'
                          : 'bg-black/[0.04] text-[#8E8E93]'
                      }`}>
                        {book.source === 'parent_request' ? 'Parent Request' : 'Bookstore'}
                      </span>
                    </div>
                  </div>

                  {/* Availability columns */}
                  <div className="flex items-start gap-3 shrink-0">
                    {/* SMCL */}
                    <div className="text-center w-16">
                      <p className="text-[10px] font-medium text-[#8E8E93] mb-1">SMCL</p>
                      <AvailabilityToggle
                        value={book.smcl_available}
                        onChange={v => handleSetAvailability(book.id, 'smcl', v)}
                        disabled={isActioning}
                      />
                      {hasIsbn && (
                        <a
                          href={smclSearchUrl(book.isbn!)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-[#1A1A1A] hover:underline mt-1 block"
                        >
                          Check
                        </a>
                      )}
                    </div>

                    {/* NYPL */}
                    <div className="text-center w-16">
                      <p className="text-[10px] font-medium text-[#8E8E93] mb-1">NYPL</p>
                      <AvailabilityToggle
                        value={book.nypl_available}
                        onChange={v => handleSetAvailability(book.id, 'nypl', v)}
                        disabled={isActioning}
                      />
                      {hasIsbn && (
                        <a
                          href={nyplSearchUrl(book.isbn!)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-[#1A1A1A] hover:underline mt-1 block"
                        >
                          Check
                        </a>
                      )}
                    </div>

                    {/* Assign agent */}
                    <div className="text-center w-28">
                      <p className="text-[10px] font-medium text-[#8E8E93] mb-1">Assign</p>
                      <select
                        onChange={e => { if (e.target.value) handleAssignAgent(book.id, e.target.value); }}
                        disabled={isActioning}
                        defaultValue=""
                        className="w-full px-2 py-1.5 bg-white border border-black/[0.08] rounded-lg text-[#1C1C1E] text-xs focus:outline-none focus:border-[#1A1A1A]"
                      >
                        <option value="" disabled>Select agent</option>
                        {agents.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Expandable library details */}
                <button
                  onClick={() => setExpandedBook(isExpanded ? null : book.id)}
                  className="flex items-center gap-1 text-xs text-[#1A1A1A] mt-2 hover:underline"
                >
                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Library details
                </button>

                {isExpanded && (
                  <LibraryDetails
                    bookId={book.id}
                    data={book.library_data}
                    onSave={data => handleSaveLibraryData(book.id, data)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Availability Toggle (Yes / No / Unchecked)
// ---------------------------------------------------------------------------

function AvailabilityToggle({ value, onChange, disabled }: {
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  disabled: boolean;
}) {
  // Cycle: null → true → false → null
  const handleClick = () => {
    if (value === null) onChange(true);
    else if (value === true) onChange(false);
    else onChange(null);
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors disabled:opacity-30 ${
        value === true
          ? 'bg-[#34C759]/15 text-[#34C759]'
          : value === false
            ? 'bg-[#FF3B30]/10 text-[#FF3B30]'
            : 'bg-black/[0.03] text-[#8E8E93]'
      }`}
    >
      {value === true ? 'Yes' : value === false ? 'No' : '—'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Library Details (expandable per book)
// ---------------------------------------------------------------------------

function LibraryDetails({ bookId, data, onSave }: {
  bookId: string;
  data: Record<string, unknown> | null;
  onSave: (data: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    smcl_branches: (data?.smcl_branches as string) || '',
    smcl_copies: (data?.smcl_copies as string) || '',
    smcl_holds: (data?.smcl_holds as string) || '',
    nypl_branches: (data?.nypl_branches as string) || '',
    nypl_copies: (data?.nypl_copies as string) || '',
    nypl_holds: (data?.nypl_holds as string) || '',
    notes: (data?.notes as string) || '',
  });

  const handleSave = () => {
    onSave(form);
  };

  const field = (label: string, key: keyof typeof form) => (
    <div>
      <label className="text-[10px] font-medium text-[#8E8E93] block mb-0.5">{label}</label>
      <input
        type="text"
        value={form[key]}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
        className="w-full px-2 py-1.5 bg-white border border-black/[0.08] rounded-lg text-[#1C1C1E] text-xs focus:outline-none focus:border-[#1A1A1A]"
        placeholder="—"
      />
    </div>
  );

  return (
    <div className="mt-3 p-3 rounded-xl bg-black/[0.02] border border-black/[0.04] space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[#636366]">San Mateo County</p>
          {field('Branches', 'smcl_branches')}
          {field('Copies available', 'smcl_copies')}
          {field('Holds', 'smcl_holds')}
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[#636366]">New York Public Library</p>
          {field('Branches', 'nypl_branches')}
          {field('Copies available', 'nypl_copies')}
          {field('Holds', 'nypl_holds')}
        </div>
      </div>
      {field('Notes', 'notes')}
      <button
        onClick={handleSave}
        className="px-3 py-1.5 rounded-lg bg-[#111111] text-[#1A1A1A] text-xs font-medium hover:bg-[#111111]/90"
      >
        Save Details
      </button>
    </div>
  );
}

export default ScanQueue;
